import { fetchAuthSession } from 'aws-amplify/auth'
import { get, post, put, del, patch } from 'aws-amplify/api'
import { store } from '../..'
import { setAuthedUser } from '../../actions/authedUser'

let didForceLogout = false

function forceLogoutOnce() {
  if (didForceLogout) return
  didForceLogout = true
  const state = store.getState()
  if (state?.authedUser != null) {
    store.dispatch(setAuthedUser(null))
  }
}

async function getAuthHeaders() {
  const colteneBranchUuid = localStorage.getItem('COLTENE_BRANCH_UUID')
  try {
    const session = await fetchAuthSession()
    const jwt = session?.tokens?.accessToken?.toString()
    return {
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(colteneBranchUuid ? { 'X-Coltene-Branch-Uuid': colteneBranchUuid } : {}),
    }
  } catch {
    return {
      ...(colteneBranchUuid ? { 'X-Coltene-Branch-Uuid': colteneBranchUuid } : {}),
    }
  }
}

async function unwrapJson(restOperation) {
  const res = await restOperation.response
  if (res.statusCode === 204 || res.statusCode === 205) return null

  const text = await res.body.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function validateSessionOrThrow() {
  try {
    const session = await fetchAuthSession()
    const hasToken = !!session?.tokens?.accessToken
    if (!hasToken) {
      forceLogoutOnce()
      throw new Error('NO_SESSION')
    }

    didForceLogout = false
    return true
  } catch (e) {
    forceLogoutOnce()
    throw e?.message ? e : new Error('NO_SESSION')
  }
}

async function trimDoubleSlashes(path = '') {
  path = String(path)
    .trim()
    .replace(/\/{2,}/g, '/')
  if (!path.startsWith('/')) path = `/${path}`
  return path
}

async function requestJson(makeRequestFn) {
  await validateSessionOrThrow()
  try {
    const op = await makeRequestFn()
    return await unwrapJson(op)
  } catch (e) {
    const msg = String(e?.message || e || '')
    if (
      msg.includes('401') ||
      msg.toLowerCase().includes('unauthorized') ||
      msg.toLowerCase().includes('no credentials') ||
      msg.toLowerCase().includes('missing identity')
    ) {
      forceLogoutOnce()
    }
    throw e
  }
}

function buildQueryString(baseParams) {
  if (!baseParams) return ''
  const sp = new URLSearchParams()
  Object.entries(baseParams).forEach(([key, value]) => {
    if (value == null) return
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v == null) return
        sp.append(key, String(v))
      })
    } else {
      sp.append(key, String(value))
    }
  })
  return sp.toString()
}

export async function _baseGetFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  const cleanPath = await trimDoubleSlashes(path)
  const qs = buildQueryString(baseParams)
  const finalPath = qs ? `${cleanPath}?${qs}` : cleanPath
  return requestJson(async () =>
    get({
      apiName,
      path: finalPath,
      options: {
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _baseGetFetchNoLang(path, baseParams, api) {
  const apiName = selectApi(api)
  const cleanPath = await trimDoubleSlashes(path)
  const qs = buildQueryString(baseParams)
  const finalPath = qs ? `${cleanPath}?${qs}` : cleanPath
  return requestJson(async () =>
    get({
      apiName,
      path: finalPath,
      options: {
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _basePostFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  return requestJson(async () =>
    post({
      apiName,
      path: await trimDoubleSlashes(path),
      options: {
        body: baseParams,
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _basePutFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  return requestJson(async () =>
    put({
      apiName,
      path: await trimDoubleSlashes(path),
      options: {
        body: baseParams,
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _basePatchFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  return requestJson(async () =>
    patch({
      apiName,
      path: await trimDoubleSlashes(path),
      options: {
        body: baseParams,
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _baseDeleteFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  const cleanPath = await trimDoubleSlashes(path)
  const qs = buildQueryString(baseParams)
  const finalPath = qs ? `${cleanPath}?${qs}` : cleanPath
  return requestJson(async () =>
    del({
      apiName,
      path: finalPath,
      options: {
        headers: await getAuthHeaders(),
      },
    }),
  )
}

export async function _baseDeleteWithBodyFetch(path, baseParams, api) {
  const apiName = selectApi(api)
  return requestJson(async () =>
    del({
      apiName,
      path: await trimDoubleSlashes(path),
      options: {
        body: baseParams,
        headers: await getAuthHeaders(),
      },
    }),
  )
}

const selectApi = (name) => {
  switch (name) {
    case ApiEnum.instrumentTracking:
      return 'mySciCanTrackingApiGateway'
    case ApiEnum.records:
      return 'mySciCanRecordsApiGateway'
    case ApiEnum.telemetry:
      return 'mySciCanTelemetryApiGateway'
    case ApiEnum.admin:
      return 'mySciCanAdminApiGateway'
    case ApiEnum.store:
      return 'mySciCanApiGatewayStore'
    case ApiEnum.ai:
      return 'mySciCanApiGatewayAi'
    default:
      return 'mySciCanApiGateway'
  }
}

export const ApiEnum = Object.freeze({
  instrumentTracking: 'instrumentTracking',
  records: 'records',
  telemetry: 'telemetry',
  store: 'store',
  admin: 'admin',
  ai: 'ai',
})

